import React, { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { motion, AnimatePresence } from "@/lib/motion";
import { Plus, X, Check, ShoppingBag } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { Reveal } from "@/components/Reveal";

export default function BuildABox() {
    const { addBox } = useCart();
    const { data: config } = useQuery({ queryKey: ["config"], queryFn: async () => (await api.get("/config")).data });
    const { data: products } = useQuery({ queryKey: ["products"], queryFn: async () => (await api.get("/products")).data });

    const tiers = config?.box_tiers || [];
    const sizes = config?.sizes || [{ key: "cup12", label: "12oz Cup", modifier: 0 }];
    const [sizeKey, setSizeKey] = useState("cup12");
    const size = sizes.find((s) => s.key === sizeKey) || sizes[0];
    const rate = size ? (size.absolute != null ? size.absolute : 12) : 12;
    const boxPrice = (t) => t.slots * rate;
    const [tierKey, setTierKey] = useState(null);
    const tier = tiers.find((t) => t.key === tierKey);
    const [slots, setSlots] = useState([]);

    // initialize default tier once loaded
    React.useEffect(() => {
        if (!tierKey && tiers.length) setTierKey(tiers[0].key);
    }, [tiers, tierKey]);

    const filled = slots.length;
    const capacity = tier?.slots || 0;
    const isFull = capacity > 0 && filled >= capacity;

    const addFlavor = (name) => {
        if (!tier) return;
        if (filled >= capacity) {
            toast("Box is full", { description: "Remove a cup or pick a bigger box." });
            return;
        }
        setSlots((s) => [...s, name]);
    };
    const removeSlot = (idx) => setSlots((s) => s.filter((_, i) => i !== idx));

    const changeTier = (key) => {
        setTierKey(key);
        const cap = tiers.find((t) => t.key === key)?.slots || 0;
        setSlots((s) => s.slice(0, cap));
    };

    const addToCart = () => {
        if (!isFull) {
            toast("Fill your box first", { description: `Add ${capacity - filled} more cup${capacity - filled > 1 ? "s" : ""}.` });
            return;
        }
        addBox({ box_key: tier.key, size_key: sizeKey, size_label: size.label, label: tier.label, flavors: slots, price: boxPrice(tier), quantity: 1 });
        toast.success(`${tier.label} added to cart 🎁`, { description: `${capacity} × ${size.label} · $${boxPrice(tier).toFixed(2)}` });
        setSlots([]);
    };

    const flavorList = useMemo(() => products || [], [products]);

    return (
        <div className="mx-auto max-w-7xl px-5 py-14 lg:px-8">
            <Reveal>
                <p className="text-sm font-semibold uppercase tracking-widest text-caramel">Build a Pearl Box</p>
                <h1 className="mt-2 font-serif text-4xl font-semibold text-roast sm:text-5xl">Craft your perfect variety box</h1>
                <p className="mt-3 max-w-xl text-mocha">Pick a cup size, choose a box, then tap to add your favorite flavors. Watch your box fill up and add it to your cart.</p>
            </Reveal>

            {/* Step 1 — cup size */}
            <p className="mt-10 text-sm font-semibold uppercase tracking-widest text-caramel">1 · Choose cup size</p>
            <div className="mt-3 flex flex-wrap gap-3">
                {sizes.map((s) => {
                    const r = s.absolute != null ? s.absolute : 12;
                    return (
                        <button
                            key={s.key}
                            onClick={() => setSizeKey(s.key)}
                            data-testid={`box-size-${s.key}`}
                            className={`rounded-2xl border px-5 py-3 text-left transition-all ${sizeKey === s.key ? "border-caramel bg-banana/20 shadow-warm" : "border-caramel/15 bg-white hover:border-caramel/40"}`}
                        >
                            <span className="block font-medium text-roast">{s.label}</span>
                            <span className="text-xs text-mocha">${r.toFixed(2)} / {/jar/i.test(s.label) ? "jar" : "cup"}</span>
                        </button>
                    );
                })}
            </div>

            {/* Step 2 — box size selector */}
            <p className="mt-8 text-sm font-semibold uppercase tracking-widest text-caramel">2 · Choose your box</p>
            <div className="mt-3 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                {tiers.map((t) => (
                    <button
                        key={t.key}
                        onClick={() => changeTier(t.key)}
                        data-testid={`box-tier-${t.key}`}
                        className={`rounded-3xl border p-5 text-left transition-all ${tierKey === t.key ? "border-caramel bg-banana/15 shadow-warm" : "border-caramel/15 bg-white hover:border-caramel/40"}`}
                    >
                        <div className="flex items-center justify-between">
                            <span className="font-serif text-xl text-roast">{t.label}</span>
                            {tierKey === t.key && <Check size={18} className="text-caramel" />}
                        </div>
                        <p className="mt-1 text-sm text-mocha">{t.desc}</p>
                        <p className="mt-3 font-serif text-2xl text-caramel" data-testid={`box-tier-price-${t.key}`}>${boxPrice(t).toFixed(2)}</p>
                    </button>
                ))}
            </div>

            <div className="mt-12 grid gap-10 lg:grid-cols-2">
                {/* Box visualization */}
                <div className="lg:sticky lg:top-24 lg:self-start">
                    <div className="rounded-[2rem] border border-caramel/15 bg-gradient-to-b from-cream-100 to-cream-200 p-6 shadow-warm">
                        <div className="flex items-center justify-between">
                            <h3 className="font-serif text-2xl text-roast">{tier?.label || "Your Box"}{tier ? ` · ${size.label}` : ""}</h3>
                            <span data-testid="box-fill-count" className="rounded-full bg-roast px-3 py-1 text-sm font-medium text-cream-50">{filled}/{capacity}</span>
                        </div>

                        <div className={`mt-5 grid gap-3 ${capacity <= 4 ? "grid-cols-2" : "grid-cols-3"}`}>
                            {Array.from({ length: capacity }).map((_, i) => (
                                <div key={i} data-testid={`box-slot-${i}`} className="relative aspect-square overflow-hidden rounded-2xl border-2 border-dashed border-caramel/25 bg-cream-50">
                                    <AnimatePresence>
                                        {slots[i] ? (
                                            <motion.button
                                                initial={{ scale: 0.6, opacity: 0 }}
                                                animate={{ scale: 1, opacity: 1 }}
                                                exit={{ scale: 0.6, opacity: 0 }}
                                                onClick={() => removeSlot(i)}
                                                className="group absolute inset-0 grid place-items-center bg-banana/25 p-2 text-center"
                                            >
                                                <span className="text-xs font-medium leading-tight text-roast">{slots[i]}</span>
                                                <span className="absolute right-1.5 top-1.5 grid h-6 w-6 place-items-center rounded-full bg-roast/80 text-cream-50 opacity-0 transition-opacity group-hover:opacity-100"><X size={13} /></span>
                                            </motion.button>
                                        ) : (
                                            <div className="absolute inset-0 grid place-items-center text-caramel/30"><Plus size={22} /></div>
                                        )}
                                    </AnimatePresence>
                                </div>
                            ))}
                        </div>

                        <div className="mt-6 flex items-center justify-between border-t border-caramel/15 pt-5">
                            <div>
                                <p className="text-sm text-mocha">Box total</p>
                                <p className="font-serif text-3xl text-caramel" data-testid="box-price">${(tier ? boxPrice(tier) : 0).toFixed(2)}</p>
                            </div>
                            <button
                                onClick={addToCart}
                                disabled={!isFull}
                                data-testid="add-box-btn"
                                className="flex items-center gap-2 rounded-full bg-caramel px-6 py-3.5 font-semibold text-cream-50 transition-all hover:bg-caramel-dark active:scale-95 disabled:opacity-40"
                            >
                                <ShoppingBag size={18} /> {isFull ? "Add Box to Cart" : `Add ${capacity - filled} more`}
                            </button>
                        </div>
                    </div>
                </div>

                {/* Flavor picker */}
                <div>
                    <h3 className="mb-4 font-serif text-2xl text-roast">Choose your flavors</h3>
                    <div className="space-y-3">
                        {flavorList.map((p) => (
                            <div key={p.id} className="flex items-center gap-4 rounded-2xl border border-caramel/10 bg-white p-3">
                                <img src={p.images?.[0]} alt={p.name} className="h-16 w-16 rounded-xl object-cover" />
                                <div className="min-w-0 flex-1">
                                    <p className="font-medium text-roast">{p.name}</p>
                                    <p className="truncate text-sm text-mocha">{p.flavor}</p>
                                </div>
                                <button
                                    onClick={() => addFlavor(p.name)}
                                    disabled={isFull}
                                    data-testid={`add-flavor-${p.slug}`}
                                    className="flex items-center gap-1.5 rounded-full bg-roast px-4 py-2.5 text-sm font-medium text-cream-50 transition-all hover:bg-caramel active:scale-95 disabled:opacity-40"
                                >
                                    <Plus size={15} /> Add
                                </button>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
}
